import { useCallback } from 'react';

import { Services } from '@/api/Services';
import { Tasks } from '@/api/Tasks';
import {
  AddAdvertiserTodoTaskRequest,
  AddTodoTaskRequest,
  AdvertiserTodoTaskFilterRequest,
  AdvertiserTodoTaskResponse,
  ArtworkLink,
  ArtworkMatchFilesResponse,
  ArtworkPreviewApprovalWorklistResponse,
  AttachmentResponse,
  CampaignMonitorPicturesResponse,
  CommentRequestDataWithFiles,
  CommentResponse,
  PageResponseServiceTaskResponse,
  PageResponseTaskResponse,
  ServiceTaskDocumentsResponse,
  ServiceTaskResponse,
  TaskGetRequest,
  TodoResponse,
  TodoTaskResponse,
  UpdateAdvertiserTodoTaskRequest,
  UpdateTaskRequest,
  UpdateTaskTeamRequest,
  UpdateTodoTaskRequest,
  VisualResourcesRequest,
  VisualResourcesResponse,
} from '@/api/data-contracts';
import { ContentType } from '@/api/http-client';
import useUploadProgressService from '@/shared/api/uploadProgress.service';

export interface JourneyStepProgressResponse {
  stepId: string;
  completed: boolean;
  substeps: Record<string, boolean>;
  deliveryMethod?: string;
}

export interface JourneyProgressResponse {
  serviceType: string;
  steps: JourneyStepProgressResponse[];
  artworkStatus?: 'PENDING' | 'APPROVED' | 'REJECTED';
  responsibleSet: boolean;
  artworkUploaded: boolean;
  artworkPreviewUploaded: boolean;
}

export interface UpdateJourneyProgressRequest {
  stepId: string;
  substepId?: string;
  completed: boolean;
  deliveryMethod?: string;
}

const useTasks = () => {
  const tasksApi = useUploadProgressService(Tasks);

  const servicesApi = useUploadProgressService(Services);

  const downloadCampaignMonitorImage = useCallback(
    async (taskId: string, documentId: string, signal?: AbortSignal): Promise<Blob> => {
      const response = await servicesApi.downloadCampaignImage(taskId, documentId, {
        format: 'blob',
        signal,
      } as any);
      return response.data;
    },
    [servicesApi],
  );

  const uploadCampaignMonitorImages = useCallback(
    async (
      taskId: string,
      files: File[],
      fieldKey:
        | 'beforeDecorationFiles'
        | 'afterDecorationFiles'
        | 'beforeNeutralizationFiles'
        | 'afterNeutralizationFiles',
    ): Promise<CampaignMonitorPicturesResponse> => {
      const formData = new FormData();

      files.forEach((file) => {
        formData.append(fieldKey, file);
      });

      const response = await servicesApi.updateCampaignMonitorImages(taskId, formData as any);
      return response.data;
    },
    [servicesApi],
  );

  const deleteCampaignMonitorImage = useCallback(
    async (taskId: string, documentId: string): Promise<CampaignMonitorPicturesResponse> => {
      try {
        const response = await servicesApi.deleteCampaignImage(taskId, documentId);
        return response.data;
      } catch (error) {
        console.error('Error deleting campaign monitor image:', error);
        throw error;
      }
    },
    [servicesApi],
  );

  // Artwork matching, task-scoped. The services role cannot use the order endpoints
  // (canUpdateOrder denies it), so shapes, matches and the sketch all resolve from the task id.
  const getServiceTaskArtworkGeometry = useCallback(
    async (taskId: string, signal?: AbortSignal): Promise<string | null> => {
      const { data } = await servicesApi.getServiceTaskArtworkGeometry(taskId, { signal });
      return data ?? null;
    },
    [servicesApi],
  );

  const updateServiceTaskArtworkGeometry = useCallback(
    async (taskId: string, geometry: string): Promise<string | null> => {
      const { data } = await servicesApi.updateServiceTaskArtworkGeometry(taskId, geometry);
      return data ?? null;
    },
    [servicesApi],
  );

  const getServiceTaskBookingGeometry = useCallback(
    async (taskId: string, signal?: AbortSignal): Promise<string | null> => {
      const { data } = await servicesApi.getServiceTaskBookingGeometry(taskId, { signal });
      return data ?? null;
    },
    [servicesApi],
  );

  const updateServiceTaskBookingGeometry = useCallback(
    async (taskId: string, geometry: string): Promise<string | null> => {
      const { data } = await servicesApi.updateServiceTaskBookingGeometry(taskId, { geometry });
      return data ?? null;
    },
    [servicesApi],
  );

  const downloadArtworkMatchFile = useCallback(
    async (taskId: string, documentId: string, signal?: AbortSignal): Promise<Blob> => {
      const response = await servicesApi.downloadArtworkMatchFile(taskId, documentId, {
        format: 'blob',
        signal,
      } as any);
      return response.data;
    },
    [servicesApi],
  );

  const uploadArtworkMatchFiles = useCallback(
    async (
      taskId: string,
      files: File[],
      fieldKey: 'artworkMatchFiles',
    ): Promise<ArtworkMatchFilesResponse> => {
      const formData = new FormData();

      files.forEach((file) => {
        formData.append(fieldKey, file);
      });

      const response = await servicesApi.updateArtworkMatchFile(taskId, formData as any);
      return response.data;
    },
    [servicesApi],
  );

  const deleteArtworkMatchFile = useCallback(
    async (taskId: string, documentId: string): Promise<ArtworkMatchFilesResponse> => {
      try {
        const response = await servicesApi.deleteArtworkMatchFile(taskId, documentId);
        return response.data;
      } catch (error) {
        console.error('Error deleting artwork match file:', error);
        throw error;
      }
    },
    [servicesApi],
  );

  const getTasks = async (
    query: TaskGetRequest,
    signal?: AbortSignal | undefined,
  ): Promise<PageResponseTaskResponse> => {
    const { data } = await tasksApi.getTasks(query, { signal });
    return data;
  };

  const getServicesTasks = useCallback(
    async (
      query: TaskGetRequest,
      signal?: AbortSignal | undefined,
    ): Promise<PageResponseServiceTaskResponse> => {
      const { data } = await servicesApi.getTasksForServices(query, { signal });

      return {
        content: data.content.map((c) => {
          const row = { ...c, ...c.customFields };
          delete row.customFields;
          return row;
        }),
        totalElements: data.totalElements,
      };
    },
    [servicesApi],
  );

  const addTodoTask = useCallback(
    async (locationId: string, request: AddTodoTaskRequest): Promise<TodoTaskResponse> => {
      const { data } = await tasksApi.addTodoTask(locationId, request);
      return data;
    },
    [tasksApi],
  );

  const updateTodoTask = async (
    locationId: string,
    taskId: string,
    request: UpdateTodoTaskRequest,
  ): Promise<TodoTaskResponse> => {
    const { data } = await tasksApi.updateTodoTask(locationId, taskId, request);
    return data;
  };

  const getServiceTask = async (
    taskId: string,
    signal?: AbortSignal,
  ): Promise<ServiceTaskResponse> => {
    const { data } = await servicesApi.getServiceTask(taskId, { signal });
    return data;
  };

  const updateServiceTask = async (
    taskId: string,
    request: UpdateTaskRequest,
  ): Promise<ServiceTaskResponse> => {
    const { data } = await servicesApi.updateServiceTask(taskId, request);
    return data;
  };

  const updateServiceTaskTeam = async (
    taskId: string,
    request: UpdateTaskTeamRequest,
  ): Promise<ServiceTaskResponse> => {
    const { data } = await servicesApi.updateServiceTaskTeam(taskId, request);
    return data;
  };

  const getJourneyProgress = useCallback(
    async (
      taskIds: string[],
      signal?: AbortSignal,
    ): Promise<Record<string, JourneyProgressResponse>> => {
      const { data } = await servicesApi.request<{
        results: Record<string, JourneyProgressResponse>;
      }>({
        path: '/services/tasks/journey',
        method: 'POST',
        secure: true,
        format: 'json',
        body: { taskIds },
        type: 'application/json' as any,
        signal,
      });
      return data.results;
    },
    [servicesApi],
  );

  const updateJourneyProgress = useCallback(
    async (
      taskId: string,
      request: UpdateJourneyProgressRequest,
    ): Promise<JourneyProgressResponse> => {
      const { data } = await servicesApi.request<JourneyProgressResponse>({
        path: `/services/tasks/${taskId}/journey`,
        method: 'PATCH',
        secure: true,
        format: 'json',
        body: request,
        type: 'application/json' as any,
      });
      return data;
    },
    [servicesApi],
  );

  const getComments = async (
    resourceId: string,
    signal?: AbortSignal | undefined,
  ): Promise<Record<string, CommentResponse[]>> => {
    const { data } = await tasksApi.getComments(resourceId, { signal });
    return data;
  };

  const getServiceTaskComments = async (
    resourceId: string,
    signal?: AbortSignal | undefined,
  ): Promise<Record<string, CommentResponse[]>> => {
    const { data } = await servicesApi.getServiceTaskComments(resourceId, { signal });
    return data;
  };

  const addComment = async (
    resourceId: string,
    request: CommentRequestDataWithFiles,
  ): Promise<CommentResponse> => {
    const { data } = await tasksApi.addComment(resourceId, request, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  };

  const addServiceTaskComment = async (
    resourceId: string,
    request: CommentRequestDataWithFiles,
  ): Promise<CommentResponse> => {
    const { data } = await servicesApi.addServiceTaskComment(resourceId, request, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  };

  const getAttachmentComments = async (
    resourceId: string,
    subResourceId: string,
    signal?: AbortSignal | undefined,
  ): Promise<Record<string, CommentResponse[]>> => {
    const { data } = await tasksApi.getTaskArtworkPreviewComments(resourceId, subResourceId, {
      signal,
    });
    return data;
  };

  const getServiceTaskAttachmentComments = async (
    resourceId: string,
    subResourceId: string,
    signal?: AbortSignal | undefined,
  ): Promise<Record<string, CommentResponse[]>> => {
    const { data } = await tasksApi.getTaskArtworkPreviewComments(resourceId, subResourceId, {
      signal,
    });
    return data;
  };

  const addAttachmentComment = async (
    resourceId: string,
    subResourceId: string,
    request: CommentRequestDataWithFiles,
    signal?: AbortSignal | undefined,
  ): Promise<CommentResponse> => {
    const { data } = await tasksApi.addTaskArtworkPreviewComment(
      resourceId,
      subResourceId,
      request,
      {
        signal,
      },
    );
    return data;
  };

  const addServiceTaskAttachmentComment = async (
    resourceId: string,
    subResourceId: string,
    request: CommentRequestDataWithFiles,
    signal?: AbortSignal | undefined,
  ): Promise<CommentResponse> => {
    const { data } = await tasksApi.addTaskArtworkPreviewComment(
      resourceId,
      subResourceId,
      request,
      {
        signal,
      },
    );
    return data;
  };

  const deleteComment = async (resourceId: string): Promise<void> => {
    await tasksApi.deleteComment(resourceId);
  };

  const editServiceTaskComment = async (
    taskId: string,
    commentId: string,
    request: { message: string },
  ): Promise<void> => {
    await servicesApi.updateServiceTaskComment(taskId, commentId, request);
  };

  const deleteServiceTaskComment = async (taskId: string, commentId: string): Promise<void> => {
    await servicesApi.deleteServiceTaskComment(taskId, commentId);
  };

  const downloadTaskCommentDocument = async (
    resourceId: string,
    documentId: string,
    signal?: AbortSignal,
  ): Promise<Blob> => {
    const { data } = await servicesApi.request<Blob>({
      path: `/services/tasks/${resourceId}/comments/documents/${documentId}`,
      method: 'GET',
      secure: true,
      format: 'blob',
      signal,
    });
    return data;
  };

  const downloadServicesTaskCommentDocument = async (
    resourceId: string,
    documentId: string,
    signal?: AbortSignal,
  ): Promise<Blob> => {
    const { data } = await servicesApi.request<Blob>({
      path: `/services/tasks/${resourceId}/comments/documents/${documentId}`,
      method: 'GET',
      secure: true,
      format: 'blob',
      signal,
    });
    return data;
  };

  const getUsersForSuppliers = async (
    supplierId: string,
    request = { pageNumber: 0, numberOfItems: 50, enabled: true, search: '' },
  ) => {
    const { data } = await servicesApi.listUsersForSupplier(supplierId, request);
    return data;
  };
  const updateServiceTaskArtworkLinks = useCallback(
    async (taskId: string, links: ArtworkLink[]): Promise<ServiceTaskResponse> => {
      // PATCH /services/tasks/{taskId}/artworkLinks is `updateArtWorkLinks` on the
      // Services API class; `updateServiceTaskArtWorkLinks` does not exist on Tasks,
      // so the call threw before any request fired — artwork links silently never saved.
      const { data } = await servicesApi.updateArtWorkLinks(taskId, links);
      return data;
    },
    [servicesApi],
  );

  const getServiceTaskVisualResources = useCallback(
    async (taskId: string, signal?: AbortSignal): Promise<VisualResourcesResponse> => {
      const { data } = await servicesApi.getVisualResources(taskId, { signal });
      return data;
    },
    [servicesApi],
  );

  const updateServiceTaskVisualResources = useCallback(
    async (taskId: string, request: VisualResourcesRequest): Promise<VisualResourcesResponse> => {
      const { data } = await servicesApi.patchVisualResources(taskId, request);
      return data;
    },
    [servicesApi],
  );

  const getAdvertiserTodoTasks = async (
    filter: AdvertiserTodoTaskFilterRequest,
    signal?: AbortSignal,
  ): Promise<AdvertiserTodoTaskResponse[]> => {
    const { data } = await tasksApi.getAdvertiserTodoTasks({ filter }, { signal });
    return data;
  };

  const addAdvertiserTodoTask = async (
    request: AddAdvertiserTodoTaskRequest,
  ): Promise<AdvertiserTodoTaskResponse> => {
    const { data } = await tasksApi.addAdvertiserTodoTask(request);
    return data;
  };

  const updateAdvertiserTodoTask = async (
    taskId: string,
    request: UpdateAdvertiserTodoTaskRequest,
  ): Promise<AdvertiserTodoTaskResponse> => {
    const { data } = await tasksApi.updateAdvertiserTodoTask(taskId, request);
    return data;
  };

  const deleteAdvertiserTodoTask = async (taskId: string): Promise<AdvertiserTodoTaskResponse> => {
    const { data } = await tasksApi.deleteAdvertiserTodoTask(taskId);
    return data;
  };

  const getTaskArtworkPreviewComments = useCallback(
    async (
      taskId: string,
      attachmentId: string,
      signal?: AbortSignal | undefined,
    ): Promise<Record<string, CommentResponse[]>> => {
      const { data } = await tasksApi.getTaskArtworkPreviewComments(taskId, attachmentId, {
        signal,
      });
      return data;
    },
    [tasksApi],
  );

  const addTaskArtworkPreviewComment = useCallback(
    async (
      taskId: string,
      attachmentId: string,
      request: CommentRequestDataWithFiles,
      signal?: AbortSignal | undefined,
    ): Promise<CommentResponse> => {
      const { data } = await tasksApi.addTaskArtworkPreviewComment(taskId, attachmentId, request, {
        signal,
      });
      return data;
    },
    [tasksApi],
  );

  const downloadServiceTaskDocument = useCallback(
    async (taskId: string, documentId: string, signal?: AbortSignal): Promise<Blob> => {
      const response = await servicesApi.getServiceTaskDocument(taskId, documentId, {
        format: 'blob',
        signal,
      } as any);
      return response.data;
    },
    [servicesApi],
  );

  const getServiceTaskDocuments = useCallback(
    async (taskId: string, signal?: AbortSignal): Promise<ServiceTaskDocumentsResponse> => {
      const { data } = await servicesApi.getServiceTaskDocuments(taskId, { signal });
      return data;
    },
    [servicesApi],
  );

  const uploadServiceTaskDocuments = useCallback(
    async (taskId: string, files: File[]): Promise<ServiceTaskDocumentsResponse> => {
      const formData = new FormData();
      files.forEach((file) => formData.append('documents', file));
      const { data } = await servicesApi.uploadServiceTaskDocuments(taskId, formData as any);
      return data;
    },
    [servicesApi],
  );

  const deleteServiceTaskDocument = useCallback(
    async (taskId: string, documentId: string): Promise<ServiceTaskDocumentsResponse> => {
      const { data } = await servicesApi.deleteServiceTaskDocument(taskId, documentId);
      return data;
    },
    [servicesApi],
  );

  const uploadTaskArtworkPreviewNewVersion = useCallback(
    async (
      taskId: string,
      attachmentId: string,
      formData: FormData,
    ): Promise<AttachmentResponse> => {
      const { data } = await servicesApi.updateServiceTaskArtworkPreviewVersion(
        taskId,
        attachmentId,
        {},
        {
          body: formData,
          type: ContentType.FormData,
        } as any,
      );
      return data;
    },
    [servicesApi],
  );

  const getTaskArtworkPreviewApprovals = useCallback(
    async (
      orderNumber: string,
      signal?: AbortSignal,
    ): Promise<ArtworkPreviewApprovalWorklistResponse[]> => {
      const { data } = await servicesApi.getArtworkPreviewApprovalsByOrderNumber(
        { orderNumber },
        { signal },
      );
      return data;
    },
    [servicesApi],
  );

  const getServiceTaskTodos = useCallback(
    async (signal?: AbortSignal): Promise<TodoResponse[]> => {
      const { data } = await servicesApi.getServiceTaskTodos({ signal });
      return data;
    },
    [servicesApi],
  );

  return {
    getTasks,
    getServicesTasks,
    addTodoTask,
    updateTodoTask,
    getServiceTask,
    updateServiceTask,
    updateServiceTaskTeam,
    getComments,
    getServiceTaskComments,
    addComment,
    addServiceTaskComment,
    getAttachmentComments,
    getServiceTaskAttachmentComments,
    addAttachmentComment,
    addServiceTaskAttachmentComment,
    deleteComment,
    editServiceTaskComment,
    deleteServiceTaskComment,
    downloadTaskCommentDocument,
    downloadServicesTaskCommentDocument,
    downloadCampaignMonitorImage,
    uploadCampaignMonitorImages,
    deleteCampaignMonitorImage,
    getServiceTaskArtworkGeometry,
    updateServiceTaskArtworkGeometry,
    getServiceTaskBookingGeometry,
    updateServiceTaskBookingGeometry,
    downloadArtworkMatchFile,
    uploadArtworkMatchFiles,
    deleteArtworkMatchFile,
    getUsersForSuppliers,
    updateServiceTaskArtworkLinks,
    getServiceTaskVisualResources,
    updateServiceTaskVisualResources,
    downloadServiceTaskDocument,
    getServiceTaskDocuments,
    uploadServiceTaskDocuments,
    deleteServiceTaskDocument,
    getAdvertiserTodoTasks,
    addAdvertiserTodoTask,
    updateAdvertiserTodoTask,
    deleteAdvertiserTodoTask,
    getJourneyProgress,
    updateJourneyProgress,
    uploadTaskArtworkPreviewNewVersion,
    getTaskArtworkPreviewComments,
    addTaskArtworkPreviewComment,
    getTaskArtworkPreviewApprovals,
    getServiceTaskTodos,
  };
};

export default useTasks;
